import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import tenantService from '../../../tenants/tenantService';
import roleCatalogService from '../../../roleCatalog/roleCatalogService';

const TenantMemberEdit = () => {
  const { membershipId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );
  const [member, setMember] = useState<any>(null);
  const [roleTemplates, setRoleTemplates] = useState<any[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    roleCatalogService.listEnabled().then((response) => {
      if (response.hasError) return;
      const enabled = response.records ?? [];
      const fromEnabled = enabled
        .map((x: any) => {
          const t = x.roleTemplate ?? x.RoleTemplate;
          if (!t) return null;
          return {
            id: t.id ?? t.Id,
            name: t.name ?? t.Name,
            enabledRoleId: x.id ?? x.Id,
          };
        })
        .filter(Boolean);
      if (fromEnabled.length > 0) {
        setRoleTemplates(fromEnabled);
        return;
      }
      roleCatalogService.listTemplates().then((r) => {
        if (!r.hasError) {
          setRoleTemplates(
            (r.records ?? []).map((t: any) => ({
              id: t.id ?? t.Id,
              name: t.name ?? t.Name,
            }))
          );
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!activeTenantId || !membershipId) return;
    dispatch({ type: 'SHOW_QUERY' });
    tenantService
      .listMembers(activeTenantId)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        const found = (response.records ?? []).find(
          (x: any) =>
            String(x.membershipId ?? x.MembershipId) === String(membershipId)
        );
        if (!found) {
          enqueueSnackbar('Tag nem található', { variant: 'error' });
          navigate('/authority/users');
          return;
        }
        setMember({
          membershipId: found.membershipId ?? found.MembershipId,
          userId: found.userId ?? found.UserId,
          fullName: found.fullName ?? found.FullName ?? '',
          email: found.email ?? found.Email ?? '',
          status: found.status ?? found.Status ?? '',
        });
        const ids =
          found.roleTemplateIds ?? found.RoleTemplateIds ?? [];
        setSelectedTemplateIds((Array.isArray(ids) ? ids : []).map(String));
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, [activeTenantId, membershipId]);

  const onSave = () => {
    if (!activeTenantId || !membershipId) return;
    setSaving(true);
    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .setMemberRoles(activeTenantId, membershipId, selectedTemplateIds)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        enqueueSnackbar('Szerepkörök mentve', { variant: 'success' });
        navigate('/authority/users');
      })
      .finally(() => {
        setSaving(false);
        dispatch({ type: 'HIDE' });
      });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <h2>Tag szerepkörei</h2>
          <Typography variant="body2" color="text.secondary">
            Szerepkör-sablonok a tagságon.
          </Typography>
        </Grid>
        {member && (
          <>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Név"
                value={member.fullName}
                disabled
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Email"
                value={member.email}
                disabled
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Státusz"
                value={member.status}
                disabled
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                multiple
                options={roleTemplates}
                value={roleTemplates.filter((t) =>
                  selectedTemplateIds.includes(String(t.id))
                )}
                getOptionLabel={(o: any) => o.name ?? String(o.id)}
                isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                onChange={(_e, selected: any[]) =>
                  setSelectedTemplateIds(selected.map((x) => String(x.id)))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Szerepkörök" />
                )}
              />
            </Grid>
          </>
        )}
        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            disabled={saving || !member}
            onClick={onSave}
          >
            Mentés
          </Button>
          <Button variant="outlined" onClick={() => navigate('/authority/users')}>
            Mégse
          </Button>
        </Grid>
        {!activeTenantId && (
          <Grid size={12}>
            <Box sx={{ color: 'warning.main' }}>
              Nincs kiválasztott szervezet — válassz a fejlécben.
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default TenantMemberEdit;
